import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { queryClient } from "@/queryClient";
import {
  getAvailableBanks,
  getBankAccountFilter,
  getBudgetBalanceStatistics,
  getBudgetFinanceBreakdown,
  getBudgetMetadata,
  getBudgetServiceMetadata,
  getBudgetSettings,
  getBudgetSummary,
  getBudgetTransactionAvailableCategories,
  getBudgetTransactionMetadata,
  getBudgetTransactions,
  getBudgetWeeklyExpenseStatistics,
  getCustomerBudgetPreferences,
  getCustomTransactionCategory,
  getDetailedBalanceStatistics,
  getExpenseComparisonStatistics,
  getExpenseComparisonStatisticsExpenseCategories,
  getExpenseComparisonStatisticsPartners,
  getExpensePlan,
  getExpensePlanBankAccounts,
  getExpensePlanCategories,
  getExpensePlanIntervals,
  getExpensePlanPeriods,
  getExpensePlans,
  getExpensePlanTransactions,
  getNewAccounts,
  getSavingsPlanPeriods,
  getSavingsPlans,
  getTransaction,
  getTransactionModificationActionCategories,
  retrieveSavingsPlanBankAccounts,
} from "./services";
import {
  type IBudgetBalanceStatisticsRequest,
  type IBudgetFinanceBreakdownRequest,
  type IBudgetTransactionAvailableCategoriesRequest,
  type IBudgetTransactionMetadataRequest,
  type IBudgetTransactionsRequest,
  type IBudgetWeeklyExpenseStatisticsRequest,
  type IDetailedBalanceStatisticsRequest,
  type IExpenseComparisonStatisticsPartnersRequest,
  type IExpenseComparisonStatisticsRequest,
  type IGetCustomTransactionCategoryRequest,
  type IGetExpensePlanPeriods,
  type IGetExpensePlanRequest,
  type IGetExpensePlansRequest,
  type IGetExpensePlanTransactions,
  type IGetSavingsPlanPeriods,
  type IGetSavingsPlansRequest,
  type ITransactionRequest,
} from "./types";

// Bank data updates every 2 hours, so try to get the newest data
const budgetStaleTime = 1000 * 60 * 60 * 2;

export const useBudgetServiceMetadataQuery = () =>
  useQuery({
    queryKey: ["budget", "metadata", "service"],
    queryFn: getBudgetServiceMetadata,
    staleTime: budgetStaleTime,
  });

export const useBudgetMetadataQuery = () =>
  useQuery({
    queryKey: ["budget", "metadata"],
    queryFn: getBudgetMetadata,
    staleTime: budgetStaleTime,
  });

export const useBudgetTransactionMetadataQuery = (
  data: IBudgetTransactionMetadataRequest,
) =>
  useQuery({
    queryKey: ["budget", "metadata", "transactions", data.bankAccountId],
    queryFn: () => getBudgetTransactionMetadata(data),
    staleTime: budgetStaleTime,
  });

export const useBudgetSummaryQuery = () =>
  useQuery({
    queryKey: ["budget", "summary"],
    queryFn: getBudgetSummary,
    staleTime: budgetStaleTime,
  });

export const useBudgetSettingsQuery = () =>
  useQuery({
    queryKey: ["budget", "settings"],
    queryFn: getBudgetSettings,
    staleTime: budgetStaleTime,
  });

export const useBudgetBalanceStatisticsQuery = (
  data: IBudgetBalanceStatisticsRequest,
) =>
  useQuery({
    queryKey: [
      "budget",
      "statistics",
      data.bankAccountId,
      format(data.date, "yyyy-MM-dd"),
    ],
    queryFn: () => getBudgetBalanceStatistics(data),
    staleTime: budgetStaleTime,
  });

export const useBankAccountFilterQuery = (enabled?: boolean) =>
  useQuery({
    queryKey: ["budget", "bankAccountFilter"],
    queryFn: getBankAccountFilter,
    staleTime: budgetStaleTime,
    enabled: enabled,
  });

export const useBudgetWeeklyExpenseStatisticsQuery = (
  data: IBudgetWeeklyExpenseStatisticsRequest,
) =>
  useQuery({
    queryKey: [
      "budget",
      "weeklyExpenseStatistics",
      data.bankAccountId,
      format(data.dateFrom, "yyyy-MM-dd"),
    ],
    queryFn: () => getBudgetWeeklyExpenseStatistics(data),
    placeholderData: keepPreviousData,
    staleTime: budgetStaleTime,
  });

export const useBudgetFinanceBreakdownQuery = (
  data: IBudgetFinanceBreakdownRequest,
) =>
  useQuery({
    queryKey: [
      "budget",
      "financeBreakdown",
      data.bankAccountId,
      format(data.dateFrom, "yyyy-MM-dd"),
      format(data.dateTo, "yyyy-MM-dd"),
      data.maxCategories,
    ],
    queryFn: () => getBudgetFinanceBreakdown(data),
    staleTime: budgetStaleTime,
  });

export const useBudgetTransactionsQuery = (data: IBudgetTransactionsRequest) =>
  useQuery({
    queryKey: [
      "budget",
      "transactions",
      data.dateFilterType,
      data.bankAccountId,
      data.transactionCategorySlug,
      data.transactionTypeId,
      data.searchText,
      format(data.date ?? new Date(), "yyyy-MM-dd"),
      format(data.periodFrom ?? new Date(), "yyyy-MM-dd"),
      format(data.periodTo ?? new Date(), "yyyy-MM-dd"),
    ],
    queryFn: () => getBudgetTransactions(data),
    staleTime: budgetStaleTime,
  });

export const useBudgetTransactionAvailableCategoriesQuery = (
  data: IBudgetTransactionAvailableCategoriesRequest,
) =>
  useQuery({
    queryKey: [
      "budget",
      "transactions",
      "availableCategories",
      data.bankAccountTransactionId,
    ],
    queryFn: () => getBudgetTransactionAvailableCategories(data),
    staleTime: budgetStaleTime,
  });

export const useGetTransactionModificationActionCategories = () =>
  useQuery({
    queryKey: ["budget", "transactions", "modificationActionCategories"],
    queryFn: getTransactionModificationActionCategories,
    staleTime: budgetStaleTime,
  });

export const useGetNewAccountsQuery = () =>
  useQuery({
    queryKey: ["budget", "newAccounts"],
    queryFn: getNewAccounts,
    staleTime: budgetStaleTime,
  });

export const useGetAvailableBanksQuery = () =>
  useQuery({
    queryKey: ["budget", "availableBanks"],
    queryFn: getAvailableBanks,
    staleTime: budgetStaleTime,
  });

export const useGetSavingsPlansQuery = (
  data: IGetSavingsPlansRequest,
  shouldKeepPreviousData = true,
) =>
  useQuery({
    queryKey: [
      "budget",
      "savingsPlans",
      data.savingsPlanStateId,
      data.maximumSavingsPlans,
    ],
    queryFn: () => getSavingsPlans(data),
    placeholderData: shouldKeepPreviousData ? keepPreviousData : undefined,
    staleTime: budgetStaleTime,
  });

export const useSavingsPlanBankAccountsQuery = () =>
  useQuery({
    queryKey: ["budget", "savingsPlans", "bankAccounts"],
    queryFn: () => retrieveSavingsPlanBankAccounts(),
    staleTime: budgetStaleTime,
  });

export const useGetSavingsPlanPeriodsQuery = (data: IGetSavingsPlanPeriods) =>
  useQuery({
    queryKey: [
      "budget",
      "savingsPlan",
      data.savingsPlanId,
      "periods",
      data.elementCount,
      data.stepBack,
    ],
    queryFn: () => getSavingsPlanPeriods(data),
    placeholderData: keepPreviousData,
    staleTime: budgetStaleTime,
  });

export const useGetExpensePlansQuery = (
  data: IGetExpensePlansRequest,
  shouldKeepPreviousData = true,
) =>
  useQuery({
    queryKey: [
      "budget",
      "expensePlans",
      data.expensePlanStateId,
      data.maximumExpensePlans,
    ],
    queryFn: () => getExpensePlans(data),
    placeholderData: shouldKeepPreviousData ? keepPreviousData : undefined,
    staleTime: budgetStaleTime,
  });

export const useGetExpensePlanQuery = (data: IGetExpensePlanRequest) =>
  useQuery({
    queryKey: ["budget", "expensePlan", data.expensePlanId],
    queryFn: () => getExpensePlan(data),
    staleTime: budgetStaleTime,
  });

export const useGetExpensePlanIntervalsQuery = () =>
  useQuery({
    queryKey: ["budget", "expensePlans", "intervals"],
    queryFn: getExpensePlanIntervals,
    staleTime: budgetStaleTime,
  });

export const useGetExpensePlanCategoriesQuery = () =>
  useQuery({
    queryKey: ["budget", "expensePlans", "categories"],
    queryFn: getExpensePlanCategories,
    staleTime: budgetStaleTime,
  });

export const useGetExpensePlanTransactionsQuery = (
  data: IGetExpensePlanTransactions,
) =>
  useQuery({
    queryKey: [
      "budget",
      "expensePlan",
      data.expensePlanId,
      "transactions",
      data.pageIndex,
      data.pageSize,
    ],
    queryFn: () => getExpensePlanTransactions(data),
    placeholderData: keepPreviousData,
    staleTime: budgetStaleTime,
  });

export const useGetExpensePlanPeriodsQuery = (data: IGetExpensePlanPeriods) =>
  useQuery({
    queryKey: [
      "budget",
      "expensePlan",
      data.expensePlanId,
      "periods",
      data.elementCount,
      data.stepBack,
    ],
    queryFn: () => getExpensePlanPeriods(data),
    placeholderData: keepPreviousData,
    staleTime: budgetStaleTime,
  });

export const useGetTransaction = (data: ITransactionRequest) =>
  useQuery({
    queryKey: ["budget", "transaction", data.bankAccountTransactionId],
    queryFn: () => getTransaction(data),
    staleTime: budgetStaleTime,
  });

export const useGetDetailedBalanceStatistics = (
  data: IDetailedBalanceStatisticsRequest,
) =>
  useQuery({
    queryKey: [
      "budget",
      "detailedBalanceStatistics",
      data.bankAccountId,
      data.date,
    ],
    queryFn: () => getDetailedBalanceStatistics(data),
    placeholderData: keepPreviousData,
    staleTime: budgetStaleTime,
  });

export const useExpensePlanBankAccountsQuery = () =>
  useQuery({
    queryKey: ["budget", "expensePlan", "bankAccounts"],
    queryFn: () => getExpensePlanBankAccounts(),
    staleTime: budgetStaleTime,
  });

export const useExpenseComparisonStatisticsPartnersQuery = (
  data: IExpenseComparisonStatisticsPartnersRequest,
) =>
  useQuery({
    queryKey: [
      "budget",
      "expenseComparisonStatistics",
      "partners",
      data.elementsCount,
      format(data.date, "yyyy-MM-dd"),
      data.bankAccountId,
      data.transactionCategoryId,
    ],
    queryFn: () => getExpenseComparisonStatisticsPartners(data),
    staleTime: budgetStaleTime,
  });

export const useExpenseComparisonStatisticsExpenseCategoriesQuery = () =>
  useQuery({
    queryKey: ["budget", "expenseComparisonStatistics", "expenseCategories"],
    queryFn: () => getExpenseComparisonStatisticsExpenseCategories(),
    staleTime: budgetStaleTime,
  });

export const useExpenseComparisonStatisticsQuery = (
  data: IExpenseComparisonStatisticsRequest,
) =>
  useQuery({
    queryKey: [
      "budget",
      "expenseComparisonStatistics",
      data.bankAccountId,
      data.transactionCategoryId,
      data.partnerName,
      format(data.date, "yyyy-MM-dd"),
      data.elementsCount,
    ],
    placeholderData: keepPreviousData,
    queryFn: () => getExpenseComparisonStatistics(data),
    staleTime: budgetStaleTime,
  });

export const useCustomTransactionCategoryQuery = (
  data: IGetCustomTransactionCategoryRequest,
) =>
  useQuery({
    queryKey: [
      "budget",
      "customTransactionCategory",
      data.transactionCategoryId,
    ],
    queryFn: () => getCustomTransactionCategory(data),
    staleTime: budgetStaleTime,
  });

export const fetchCustomTransactionCategoryQuery = async (
  data: IGetCustomTransactionCategoryRequest,
) =>
  await queryClient.fetchQuery({
    queryKey: [
      "budget",
      "customTransactionCategory",
      data.transactionCategoryId,
    ],
    queryFn: () => getCustomTransactionCategory(data),
    staleTime: budgetStaleTime,
  });

export const useCustomerBudgetPreferencesQuery = () =>
  useQuery({
    queryKey: ["budget", "customerPreferences"],
    queryFn: () => getCustomerBudgetPreferences(),
    staleTime: budgetStaleTime,
  });
